import { useState, useEffect } from 'react';

function OverviewTab({ setActiveTab, setStatusFilter, topLikedPrompts, stats }) {
  const [recentActivity, setRecentActivity] = useState([]);
  const [topBoards, setTopBoards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    loadAdditionalData();
  }, []);

  const loadAdditionalData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load recent activity
      const activityRes = await fetch(API_BASE_URL + '/admin/activity-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ page: 1, limit: 5 })
      });
      
      if (activityRes.ok) {
        const activityData = await activityRes.json();
        setRecentActivity(activityData.logs || []);
      }

      // Load board stats
      const boardRes = await fetch(API_BASE_URL + '/admin/board-stats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });
      
      if (boardRes.ok) {
        const boardData = await boardRes.json();
        setTopBoards(boardData.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading additional data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Vừa xong';
    if (seconds < 3600) return Math.floor(seconds / 60) + ' phút trước';
    if (seconds < 86400) return Math.floor(seconds / 3600) + ' giờ trước';
    return Math.floor(seconds / 86400) + ' ngày trước';
  };

  const getActivityIcon = (type) => {
    const icons = {
      pin_approved: 'check_circle',
      pin_rejected: 'cancel',
      pin_deleted: 'delete',
      user_deleted: 'person_remove',
      user_made_admin: 'admin_panel_settings',
      view_user_profiles: 'visibility'
    };
    return icons[type] || 'info';
  };

  const getActivityColor = (type) => {
    const colors = {
      pin_approved: 'text-green-600 bg-green-50',
      pin_rejected: 'text-red-600 bg-red-50',
      pin_deleted: 'text-orange-600 bg-orange-50',
      user_deleted: 'text-red-600 bg-red-50',
      user_made_admin: 'text-purple-600 bg-purple-50',
      view_user_profiles: 'text-blue-600 bg-blue-50'
    };
    return colors[type] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tổng quan hệ thống</h2>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span className="material-symbols-rounded text-[18px] animate-pulse">schedule</span>
          <span>Cập nhật realtime</span>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <button 
          onClick={() => { setActiveTab('prompts'); setStatusFilter('pending'); }} 
          className="group p-5 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border-2 border-orange-200 dark:border-orange-800 rounded-2xl hover:shadow-xl text-left transition-all hover:scale-105 animate-fade-in-up"
        >
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
            <span className="material-symbols-rounded text-white text-3xl">pending_actions</span>
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white mb-1">Duyệt Prompts</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Xem prompts chờ duyệt</p>
          {stats?.pendingPins > 0 && (
            <div className="mt-3 inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
              <span>{stats.pendingPins}</span>
              <span>cần xử lý</span>
            </div>
          )}
        </button>
        
        <button 
          onClick={() => setActiveTab('users')} 
          className="group p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl hover:shadow-xl text-left transition-all hover:scale-105 animate-fade-in-up stagger-1"
        >
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
            <span className="material-symbols-rounded text-white text-3xl">person_add</span>
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white mb-1">Quản lý Users</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Xem danh sách users</p>
          {stats?.newUsers > 0 && (
            <div className="mt-3 inline-flex items-center gap-1 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              <span>+{stats.newUsers}</span>
              <span>tuần này</span>
            </div>
          )}
        </button>
        
        <button 
          onClick={() => setActiveTab('profiles')} 
          className="group p-5 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-2xl hover:shadow-xl text-left transition-all hover:scale-105 animate-fade-in-up stagger-2"
        >
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
            <span className="material-symbols-rounded text-white text-3xl">badge</span>
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white mb-1">Hồ sơ Users</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Xem thông tin thu thập</p>
        </button>
        
        <button 
          onClick={() => setActiveTab('activity')} 
          className="group p-5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 rounded-2xl hover:shadow-xl text-left transition-all hover:scale-105 animate-fade-in-up stagger-3"
        >
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
            <span className="material-symbols-rounded text-white text-3xl">history</span>
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white mb-1">Hoạt động</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Xem lịch sử hệ thống</p>
        </button>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg animate-fade-in-up stagger-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="material-symbols-rounded text-blue-500">history</span>
              Hoạt động gần đây
            </h3>
            <button
              onClick={() => setActiveTab('activity')}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              Xem tất cả →
            </button>
          </div>
          
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivity.length > 0 ? (
            <div className="space-y-2">
              {recentActivity.map((activity, index) => (
                <div key={activity._id} className={'flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all animate-slide-in-left stagger-' + (index + 1)}>
                  <div className={'w-10 h-10 rounded-full flex items-center justify-center ' + getActivityColor(activity.type)}>
                    <span className="material-symbols-rounded text-[20px]">{getActivityIcon(activity.type)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      <span className="font-bold">{activity.actor}</span> {activity.type.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatTimeAgo(activity.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <span className="material-symbols-rounded text-4xl text-gray-300 dark:text-gray-600 mb-2 block">inbox</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">Chưa có hoạt động</p>
            </div>
          )}
        </div>

        {/* Top Boards */}
        <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg animate-fade-in-up stagger-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="material-symbols-rounded text-indigo-500">dashboard</span>
              Top Boards
            </h3>
            <button
              onClick={() => setActiveTab('boards')}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              Xem tất cả →
            </button>
          </div>
          
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-lg"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-2/3"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : topBoards.length > 0 ? (
            <div className="space-y-2">
              {topBoards.map((board, index) => {
                const boardInfo = board.boardInfo && board.boardInfo[0];
                return (
                  <div key={board._id} className={'flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all animate-slide-in-right stagger-' + (index + 1)}>
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold shadow-md">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                        {boardInfo?.icon || '📌'} {boardInfo?.name || 'Unknown'}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>{board.count} prompts</span>
                        <span>•</span>
                        <span>{board.totalLikes || 0} likes</span>
                        <span>•</span>
                        <span>{board.totalViews || 0} views</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <span className="material-symbols-rounded text-4xl text-gray-300 dark:text-gray-600 mb-2 block">dashboard</span>
              <p className="text-sm text-gray-500 dark:text-gray-400">Chưa có board nào</p>
            </div>
          )}
        </div>
      </div>

      {/* Top 10 Liked Prompts */}
      {topLikedPrompts && topLikedPrompts.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg animate-fade-in-up stagger-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="material-symbols-rounded text-pink-500 animate-pulse-slow">favorite</span>
              Top 10 Prompts nhiều tim nhất
            </h3>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="material-symbols-rounded text-[16px]">trending_up</span>
              <span>Hot nhất</span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {topLikedPrompts.map((prompt, index) => {
              const isTop3 = index < 3;
              const badgeColor = index === 0 ? 'from-yellow-400 to-yellow-600' : index === 1 ? 'from-gray-300 to-gray-400 text-gray-800' : 'from-orange-400 to-orange-600';
              
              return (
                <div key={prompt._id} className={'flex items-center gap-4 p-4 bg-gradient-to-r rounded-xl hover:shadow-md transition-all animate-slide-in-left stagger-' + (index + 1) + (isTop3 ? ' from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 border-2 border-gray-200 dark:border-gray-600' : ' bg-gray-50 dark:bg-gray-700')}>
                  <div className={'w-10 h-10 rounded-full bg-gradient-to-br text-white flex items-center justify-center font-bold shadow-lg ' + badgeColor}>
                    {index + 1}
                  </div>
                  <img src={prompt.image} className="w-20 h-20 rounded-xl object-cover shadow-md" alt="" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 dark:text-white truncate text-base">{prompt.title || 'Untitled'}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{prompt.prompt}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">By @{prompt.user}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/30 px-3 py-2 rounded-lg">
                      <span className="material-symbols-rounded text-[18px]">favorite</span>
                      <span className="font-bold">{prompt.likes || 0}</span>
                    </div>
                    <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-3 py-2 rounded-lg">
                      <span className="material-symbols-rounded text-[18px]">visibility</span>
                      <span className="font-bold">{prompt.views || 0}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default OverviewTab;
